import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Settings, 
  Users, 
  Shield, 
  Database, 
  Bell, 
  Globe,
  Save,
  RefreshCw,
  Moon,
  Sun,
  Monitor,
  User,
  Building2,
  Palette,
  Loader2
} from "lucide-react";
import { 
  usePermissions, 
  IfCanManageUsers, 
  IfCanAccessSettings,
  ConditionalRender 
} from "@/components/auth";
import { useThemeWithBackend } from "@/hooks/useThemeWithBackend";
import { toast } from "sonner";
import { GestaoUsuariosSection } from "@/components/admin/GestaoUsuariosSection";
import { EditarMeuPerfilForm } from "@/components/perfil/EditarMeuPerfilForm";
import { getDadosEmpresa, updateDadosEmpresa } from "@/services/dadosEmpresaService";

/** Formata CNPJ para exibição: 00.000.000/0000-00 */
function formatCNPJ(value: string): string {
  const v = value.replace(/\D/g, "").slice(0, 14);
  if (v.length <= 2) return v;
  if (v.length <= 5) return v.replace(/^(\d{2})(\d+)$/, "$1.$2");
  if (v.length <= 8) return v.replace(/^(\d{2})(\d{3})(\d+)$/, "$1.$2.$3");
  if (v.length <= 12) return v.replace(/^(\d{2})(\d{3})(\d{3})(\d+)$/, "$1.$2.$3/$4");
  return v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

/** Formata telefone para exibição: (00) 00000-0000 ou (00) 0000-0000 */
function formatTelefone(value: string): string {
  const v = value.replace(/\D/g, "").slice(0, 11);
  if (v.length === 11) return v.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  if (v.length === 10) return v.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
  return v;
}

/** Formata CEP para exibição: 00000-000 */
function formatCEP(value: string): string {
  const v = value.replace(/\D/g, "").slice(0, 8);
  if (v.length <= 5) return v;
  return v.replace(/^(\d{5})(\d+)$/, "$1-$2");
}

export default function Configuracoes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = usePermissions();
  const canManageUsers = permissions.users.canManage();

  const tabFromUrl = searchParams.get("tab");
  const validTabs = canManageUsers
    ? ["perfil", "geral", "usuarios", "seguranca"]
    : ["perfil", "geral", "seguranca"];
  const activeTab = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "perfil";
  const { theme, setTheme, saveTheme, isLoading: themeLoading, isSaving: themeSaving } = useThemeWithBackend();
  
  // Estado local para o tema selecionado (ainda não salvo)
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'system'>(theme || 'light');
  // Estado para o tema salvo no backend (para comparação)
  const [savedTheme, setSavedTheme] = useState<'light' | 'dark' | 'system' | undefined>(theme);
  const prevThemeLoadingRef = useRef(themeLoading);
  
  // Inicializar savedTheme quando o componente monta e o tema já está disponível
  useEffect(() => {
    if (theme && savedTheme === undefined) {
      setSelectedTheme(theme);
      setSavedTheme(theme);
    }
  }, [theme, savedTheme]);
  
  // Sincronizar estados locais quando o tema for carregado do backend
  // Atualiza savedTheme apenas quando themeLoading muda de true para false (carregamento completo)
  useEffect(() => {
    if (prevThemeLoadingRef.current && !themeLoading && theme) {
      // Tema acabou de ser carregado do backend
      setSelectedTheme(theme);
      setSavedTheme(theme);
    }
    prevThemeLoadingRef.current = themeLoading;
  }, [themeLoading, theme]);

  const [dadosEmpresa, setDadosEmpresa] = useState({
    razaoSocial: "",
    cnpj: "",
    telefoneContato: "",
    emailContato: "",
    cep: "",
    estado: "",
    cidade: "",
    rua: "",
    numero: "",
    complemento: "",
  });
  const [dadosEmpresaLoading, setDadosEmpresaLoading] = useState(true);
  const [dadosEmpresaSaving, setDadosEmpresaSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDadosEmpresaLoading(true);
    getDadosEmpresa()
      .then((form) => {
        if (!cancelled) setDadosEmpresa(form);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err);
          toast.error("Não foi possível carregar os dados da empresa.");
        }
      })
      .finally(() => {
        if (!cancelled) setDadosEmpresaLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const isAdmin = permissions.isAdmin();

  const fetchAddressFromCEP = useCallback(async (cepValue: string) => {
    const clean = cepValue.replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setDadosEmpresa((prev) => ({
          ...prev,
          estado: data.uf ?? prev.estado,
          cidade: data.localidade ?? prev.cidade,
          rua: data.logradouro ?? prev.rua,
        }));
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      toast.error("Não foi possível buscar o endereço pelo CEP.");
    } finally {
      setCepLoading(false);
    }
  }, []);

  const handleSalvarDadosEmpresa = async () => {
    setDadosEmpresaSaving(true);
    try {
      const atualizado = await updateDadosEmpresa(dadosEmpresa);
      setDadosEmpresa(atualizado);
      toast.success("Dados da empresa salvos com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Erro ao salvar dados da empresa.");
    } finally {
      setDadosEmpresaSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Configurações</h1>
          <p className="text-muted-foreground">
            Gerencie as configurações do sistema e usuários
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            {permissions.userRole === 'admin' ? 'Administrador' : 
             permissions.userRole === 'representante' ? 'Representante' : 
             permissions.userRole === 'parceiro' ? 'Parceiro' : 'Usuário'}
          </Badge>
        </div>
      </div>

      {/* Tabs de Configurações */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className={`grid w-full ${canManageUsers ? "grid-cols-4" : "grid-cols-3"}`}>
          <TabsTrigger value="perfil">Perfil</TabsTrigger>
          <TabsTrigger value="geral">Geral</TabsTrigger>
          {canManageUsers && <TabsTrigger value="usuarios">Usuários</TabsTrigger>}
          <TabsTrigger value="seguranca">Segurança</TabsTrigger>
        </TabsList>

        {/* Meu Perfil - Edição dos dados do usuário logado (sem alterar role) */}
        <TabsContent value="perfil" className="space-y-4">
          <EditarMeuPerfilForm />
        </TabsContent>

        {/* Aba Geral */}
        <TabsContent value="geral" className="space-y-4">
          {/* Dados da Empresa - editável apenas por admin */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Dados da Empresa
              </CardTitle>
              <CardDescription>
                Informações cadastrais da empresa. Apenas administradores podem alterar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {dadosEmpresaLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  Carregando dados da empresa...
                </div>
              ) : (
                <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="razao-social">Razão Social</Label>
                  <Input
                    id="razao-social"
                    value={dadosEmpresa.razaoSocial}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, razaoSocial: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input
                    id="cnpj"
                    value={formatCNPJ(dadosEmpresa.cnpj)}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, cnpj: e.target.value.replace(/\D/g, "") }))}
                    disabled={!isAdmin}
                    placeholder="00.000.000/0000-00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone-contato">Telefone de Contato</Label>
                  <Input
                    id="telefone-contato"
                    value={formatTelefone(dadosEmpresa.telefoneContato)}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, telefoneContato: e.target.value.replace(/\D/g, "") }))}
                    disabled={!isAdmin}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-contato">Email de Contato</Label>
                  <Input
                    id="email-contato"
                    type="email"
                    value={dadosEmpresa.emailContato}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, emailContato: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP</Label>
                  <Input
                    id="cep"
                    value={formatCEP(dadosEmpresa.cep)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "").slice(0, 8);
                      setDadosEmpresa((p) => ({ ...p, cep: raw }));
                      if (raw.length === 8) fetchAddressFromCEP(raw);
                    }}
                    onBlur={() => {
                      if (dadosEmpresa.cep.replace(/\D/g, "").length === 8) fetchAddressFromCEP(dadosEmpresa.cep);
                    }}
                    disabled={!isAdmin}
                    placeholder="00000-000"
                  />
                  {cepLoading && <p className="text-xs text-muted-foreground">Buscando endereço...</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Input
                    id="estado"
                    value={dadosEmpresa.estado}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, estado: e.target.value }))}
                    disabled={!isAdmin}
                    placeholder="UF"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={dadosEmpresa.cidade}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, cidade: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="rua">Rua</Label>
                  <Input
                    id="rua"
                    value={dadosEmpresa.rua}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, rua: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="numero">Número</Label>
                  <Input
                    id="numero"
                    value={dadosEmpresa.numero}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, numero: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="complemento">Complemento</Label>
                  <Input
                    id="complemento"
                    value={dadosEmpresa.complemento}
                    onChange={(e) => setDadosEmpresa((p) => ({ ...p, complemento: e.target.value }))}
                    disabled={!isAdmin}
                  />
                </div>
              </div>
                </>
              )}

              {isAdmin && !dadosEmpresaLoading && (
                <Button
                  onClick={handleSalvarDadosEmpresa}
                  disabled={dadosEmpresaSaving}
                >
                  {dadosEmpresaSaving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Salvar Dados da Empresa
                </Button>
              )}
              {!isAdmin && (
                <p className="text-sm text-muted-foreground">
                  Apenas administradores podem alterar os dados da empresa.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Tema - todos os usuários podem alterar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Tema
              </CardTitle>
              <CardDescription>
                Escolha o tema de exibição da aplicação de acordo com sua preferência.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="theme-preference">Preferência de Tema</Label>
                <Select 
                  value={selectedTheme} 
                  onValueChange={(value: 'light' | 'dark' | 'system') => {
                    setSelectedTheme(value);
                    setTheme(value); // Aplica localmente para preview
                  }}
                  disabled={themeLoading || themeSaving}
                >
                  <SelectTrigger id="theme-preference" className="w-full">
                    <SelectValue placeholder="Selecione o tema..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">
                      <div className="flex items-center gap-2">
                        <Sun className="h-4 w-4" />
                        <span>Claro</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="dark">
                      <div className="flex items-center gap-2">
                        <Moon className="h-4 w-4" />
                        <span>Escuro</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="system">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4" />
                        <span>Sistema</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Claro, escuro ou seguir a preferência do seu sistema operacional.
                </p>
              </div>
              
              <Button
                onClick={async () => {
                  try {
                    await saveTheme(selectedTheme);
                    setSavedTheme(selectedTheme); // Atualiza o tema salvo após salvar com sucesso
                    toast.success("Tema salvo com sucesso!");
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : "Erro ao salvar tema.");
                  }
                }}
                disabled={themeSaving || (savedTheme !== undefined && selectedTheme === savedTheme)}
              >
                {themeSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar Tema
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Informações do Sistema (incorporado à aba Geral) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Informações do Sistema
              </CardTitle>
              <CardDescription>
                Versão, status e ações do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Versão do Sistema</p>
                  <p className="text-2xl font-bold">1.0.0</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Última Atualização</p>
                  <p className="text-sm">15/01/2024</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Status do Sistema</Label>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Sistema Operacional</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Verificar Atualizações
                </Button>
                <ConditionalRender 
                  show={isAdmin}
                  fallback={null}
                >
                  <Button variant="outline" size="sm">
                    <Database className="mr-2 h-4 w-4" />
                    Backup do Sistema
                  </Button>
                </ConditionalRender>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gestão de Usuários - Apenas para Admins */}
        <TabsContent value="usuarios" className="space-y-4">
          <IfCanManageUsers>
            <GestaoUsuariosSection />
          </IfCanManageUsers>

          <ConditionalRender 
            show={!permissions.users.canManage()}
            fallback={null}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Acesso Restrito
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  A gestão de usuários é restrita a administradores do sistema.
                </p>
              </CardContent>
            </Card>
          </ConditionalRender>
        </TabsContent>

        {/* Configurações de Segurança */}
        <TabsContent value="seguranca" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Segurança
              </CardTitle>
              <CardDescription>
                Configurações de segurança e autenticação
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="session-timeout">Timeout da Sessão (minutos)</Label>
                <Input 
                  id="session-timeout" 
                  type="number" 
                  defaultValue="30"
                  disabled={!permissions.settings.canUpdate()}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch 
                  id="2fa" 
                  defaultChecked 
                  disabled={!permissions.settings.canUpdate()}
                />
                <Label htmlFor="2fa">Requer autenticação de dois fatores</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch 
                  id="password-policy" 
                  defaultChecked 
                  disabled={!permissions.settings.canUpdate()}
                />
                <Label htmlFor="password-policy">Política de senhas forte</Label>
              </div>

              <ConditionalRender 
                show={permissions.settings.canUpdate()}
                fallback={
                  <div className="text-sm text-muted-foreground p-2 bg-muted rounded">
                    Você não tem permissão para alterar estas configurações
                  </div>
                }
              >
                <Button className="w-full">
                  <Save className="mr-2 h-4 w-4" />
                  Salvar Configurações de Segurança
                </Button>
              </ConditionalRender>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
